import React, { useCallback, useState, useRef } from 'react';
import { Image } from 'react-native';
import styled, { css } from '@emotion/native';
import RequestDetails from './RequestDetails';
import BookCalendar from './BookCalendar';
import AddressStep, { AddressStepHandle } from './AddressStep';
import { Portal, ProgressBar } from 'react-native-paper';
import { accent, green, sand, white } from '@/shared/styles/colors';
import BookingSummary from './BookingSummary/BookingSummary';
import { CREATE_CONSULTATION, CREATE_PAYMENT_INTENT } from '@/shared/mutations';
import { useMutation } from '@apollo/client';
import {
  MutationCreateConsultationArgs,
  MutationCreatePaymentIntentArgs,
  MutationResult,
  SessionType,
} from '@/shared/types/generated';
import { BodyText } from '@/shared/components/StyledText';
import useExploreStore from '@/stores/useExploreStore';
import { getDisabledState } from './BookingSummary/utils';
import useStore from '@/stores/useStore';
import SnackBar from '@/shared/components/SnackBar';
import SentryService from '@/services/sentry/Sentry.service';
import { getCurrentTimeZone, getRateByTime } from '@/shared/utils';
import useSendbirdChannelUrl from '@/shared/hooks/useSendbirdChannelUrl';
import ModalButton from '@/components/shared/ModalButton';
import Modal from '@/shared/components/Modal';
import ModalWrapper from '@/shared/styles/components/ModalWrapper';
import { getPreAuthDurationMinutes } from './shared/preAuth';

export enum BookingStep {
  Calendar = 'Calendar',
  Address = 'Address',
  Details = 'Details',
  Summary = 'Summary',
}

interface BookingModalProps {
  isVisible: boolean;
  setIsVisible: (isVisible: boolean) => void;
}

const BookingModal: React.FC<BookingModalProps> = ({
  isVisible,
  setIsVisible,
}) => {
  const { booking, resetBooking, selectedStylist, userHasPaymentMethod } =
    useExploreStore();
  const { setSnackbarState } = useStore();
  const { fetchChannelUrl } = useSendbirdChannelUrl();

  // Detect if this is an in-person session based on booking state
  const isInPersonSession =
    booking.sessionType === SessionType.InPersonEvent ||
    booking.sessionType === SessionType.InPersonSession;

  const [isErrorSnackbarState, setIsErrorSnackbarState] =
    useState<boolean>(false);
  const [isAddressValid, setIsAddressValid] = useState<boolean>(false);
  const [isAddressSaving, setIsAddressSaving] = useState<boolean>(false);

  const addressStepRef = useRef<AddressStepHandle>(null);

  const handleAddressValidChange = useCallback((isValid: boolean) => {
    setIsAddressValid(isValid);
  }, []);

  const onClose = () => {
    setCurrentStep(BookingStep.Calendar);
    resetBooking();
    setDiscountCode(null);
    setIsAddressValid(false);
    setIsVisible(false);
  };

  const [createPaymentIntent, { loading: paymentIntentLoading }] = useMutation<
    MutationResult<'createPaymentIntent'>,
    MutationCreatePaymentIntentArgs
  >(CREATE_PAYMENT_INTENT);

  const [createConsultation, { loading: consultationLoading }] = useMutation<
    MutationResult<'createConsultation'>,
    MutationCreateConsultationArgs
  >(CREATE_CONSULTATION, {
    fetchPolicy: 'network-only',
    onError: () => {
      setIsErrorSnackbarState(true);
    },
    onCompleted: async (data) => {
      __DEV__ && console.log(data);
      const { createConsultation } = data;
      // Only create Sendbird channel for video sessions
      if (
        !isInPersonSession &&
        createConsultation?.client?.user?.id &&
        createConsultation?.stylist?.user?.id
      ) {
        await fetchChannelUrl(
          createConsultation.stylist?.user?.id,
          createConsultation.client?.user?.id,
        );
      }

      setSnackbarState({
        open: true,
        message: isInPersonSession
          ? 'In-person session scheduled successfully!'
          : 'Session scheduled successfully!',
        severity: 'success',
      });
      onClose();
    },
  });

  const [currentStep, setCurrentStep] = useState<BookingStep>(
    BookingStep.Calendar,
  );
  const [discountCode, setDiscountCode] = useState<string | null>(null);

  // Combined loading state for both payment intent and consultation creation
  const isBookingInProgress = paymentIntentLoading || consultationLoading;

  const handleBack = () => {
    if (currentStep === BookingStep.Address) {
      setIsAddressValid(false);
      setCurrentStep(BookingStep.Calendar);
    } else if (currentStep === BookingStep.Details) {
      // Go back to Address step if in-person, otherwise Calendar
      if (!isInPersonSession) {
        setIsAddressValid(false);
      }
      setCurrentStep(
        isInPersonSession ? BookingStep.Address : BookingStep.Calendar,
      );
    } else if (currentStep === BookingStep.Summary) {
      setCurrentStep(BookingStep.Details);
    }
  };

  const getStepText = () => {
    const totalSteps = isInPersonSession ? 3 : 2;
    switch (currentStep) {
      case BookingStep.Calendar:
        return `0/${totalSteps}`;
      case BookingStep.Address:
        return `1/${totalSteps}`;
      case BookingStep.Details:
        return isInPersonSession ? `2/${totalSteps}` : `1/${totalSteps}`;
      case BookingStep.Summary:
        return `${totalSteps}/${totalSteps}`;
      default:
        return `0/${totalSteps}`;
    }
  };

  const renderStep = () => {
    switch (currentStep) {
      case BookingStep.Calendar:
        return <BookCalendar onDismissModal={onClose} />;
      case BookingStep.Address:
        return (
          <AddressStep
            ref={addressStepRef}
            onAddressValid={handleAddressValidChange}
          />
        );
      case BookingStep.Details:
        return <RequestDetails />;
      case BookingStep.Summary:
        return (
          <BookingSummary
            showHeader
            onDismissModal={onClose}
            isInPersonSession={isInPersonSession}
            onDiscountCodeChange={setDiscountCode}
          />
        );
      default:
        return null;
    }
  };

  const getProgress = () => {
    if (isInPersonSession) {
      // 4 steps for in-person: Calendar -> Address -> Details -> Summary
      switch (currentStep) {
        case BookingStep.Calendar:
          return 0;
        case BookingStep.Address:
          return 0.33;
        case BookingStep.Details:
          return 0.66;
        case BookingStep.Summary:
          return 1;
        default:
          return 0;
      }
    }
    // 3 steps for virtual: Calendar -> Details -> Summary
    switch (currentStep) {
      case BookingStep.Calendar:
        return 0;
      case BookingStep.Details:
        return 0.5;
      case BookingStep.Summary:
        return 1;
      default:
        return 0;
    }
  };

  const onContinuePress = async () => {
    switch (currentStep) {
      case BookingStep.Calendar:
        // Go to Address step for in-person, otherwise Details
        setCurrentStep(
          isInPersonSession ? BookingStep.Address : BookingStep.Details,
        );
        break;
      case BookingStep.Address: {
        setIsAddressSaving(true);
        const saveSuccess = await addressStepRef.current?.saveAddress();
        setIsAddressSaving(false);
        if (saveSuccess) {
          setCurrentStep(BookingStep.Details);
        }
        break;
      }
      case BookingStep.Details:
        setCurrentStep(BookingStep.Summary);
        break;
      case BookingStep.Summary: {
        if (
          !selectedStylist?.pricing?.hourlyRate ||
          !booking.selectedDate ||
          !booking.selectedTime
        ) {
          SentryService.captureError('Missing required data for booking');
          return;
        }
        // Calculate pre-auth amount
        // For IN_PERSON_SESSION: pre-authorize for 1.5× booked duration
        // For other sessions: pre-authorize for estimated duration + 30 minutes buffer
        const inPersonHourlyRate =
          selectedStylist.customInPersonHourlyRate ??
          selectedStylist.pricing.hourlyRate;
        const customHourlyRate =
          selectedStylist.customHourlyRate ??
          selectedStylist.pricing.hourlyRate;
        const isInPersonSessionBooking =
          booking.sessionType === SessionType.InPersonSession;
        const hourlyRate = isInPersonSessionBooking
          ? inPersonHourlyRate
          : customHourlyRate;
        const preAuthDuration = getPreAuthDurationMinutes(
          booking.sessionDuration,
          isInPersonSessionBooking,
        );
        const preAuthAmount = getRateByTime(hourlyRate, preAuthDuration);

        const paymentIntent = await createPaymentIntent({
          variables: {
            input: {
              stylistId: selectedStylist.id,
              amount: preAuthAmount,
              ...(discountCode && { discountCode }),
            },
          },
        });

        if (!paymentIntent.data?.createPaymentIntent?.id) {
          throw new Error('Missing payment intent ID');
        }

        createConsultation({
          variables: {
            input: {
              stylistId: selectedStylist.id,
              paymentId: paymentIntent.data.createPaymentIntent.id,
              isInstant: false,
              startTime: booking.selectedDate,
              estimatedDuration: booking.sessionDuration,
              clientNotes: booking.additionalDetails,
              reason: booking.reason,
              clientTimezone: getCurrentTimeZone(),
              sessionType: booking.sessionType,
            },
          },
        });
        break;
      }
      default:
        break;
    }
  };

  const isFirstStep = currentStep === BookingStep.Calendar;

  return (
    <Modal
      isVisible={isVisible}
      setIsVisible={setIsVisible}
      onDismiss={onClose}
      onRequestClose={onClose}
      mobileBackgroundColor={sand}
    >
      <Portal.Host>
        <ModalWrapper style={containerStyles}>
          <Header reverseRow={isFirstStep}>
            {!isFirstStep && (
              <IconButton onPress={handleBack}>
                <Image
                  style={{ height: 34, width: 34 }}
                  source={require('@/assets/images/BackWithCircle.png')}
                />
              </IconButton>
            )}
            <IconButton onPress={onClose}>
              <Image
                style={{ height: 20, width: 20 }}
                source={require('@/assets/images/CloseGreen.png')}
              />
            </IconButton>
          </Header>

          <ProgressContainer>
            <ProgressBar
              progress={getProgress()}
              color={isInPersonSession ? green : accent}
              style={{ height: 8, borderRadius: 256, backgroundColor: white }}
            />
            <ProgressText>{getStepText()} completed</ProgressText>
          </ProgressContainer>

          {renderStep()}
          <ModalButton
            disabled={
              getDisabledState(
                booking,
                currentStep,
                userHasPaymentMethod,
                isAddressValid,
              ) ||
              isBookingInProgress ||
              isAddressSaving
            }
            loading={isBookingInProgress || isAddressSaving}
            onPress={onContinuePress}
            text={
              currentStep === BookingStep.Summary
                ? isInPersonSession
                  ? 'Request Session'
                  : 'Schedule Call'
                : 'Continue'
            }
          />
        </ModalWrapper>
        <SnackBar
          visible={isErrorSnackbarState}
          onDismiss={() => setIsErrorSnackbarState(false)}
          message={
            isInPersonSession
              ? 'Error scheduling in-person session, please try again.'
              : 'Error scheduling session, please try again.'
          }
          severity="error"
          hasNavBar={false}
        />
      </Portal.Host>
    </Modal>
  );
};

const containerStyles = css({
  flex: 1,
  backgroundColor: sand,
});

const Header = styled.View<{ reverseRow: boolean }>(({ reverseRow }) => ({
  flexDirection: reverseRow ? 'row-reverse' : 'row',
  justifyContent: 'space-between',
  paddingBottom: 20,
  paddingHorizontal: 20,
}));

const IconButton = styled.TouchableOpacity({
  padding: 8,
});

const ProgressContainer = styled.View({
  paddingHorizontal: 16,
  marginBottom: 24,
  width: 290,
  alignSelf: 'center',
});

const ProgressText = styled(BodyText)({
  fontSize: 10,
  color: green,
  marginTop: 12,
  textAlign: 'center',
});

export default BookingModal;
